/**
 * Constraints copied from the GCDM interface specification (customers.yaml,
 * schema `BusinessPartner`). They are enforced by GCDM on both `POST` and `PUT /customers`,
 * so a violation makes the write fail with a generic 400. CDF checks them up front to be able
 * to point at the offending field (CUSTAPP-612).
 *
 * Keep in sync with `GcdmConstraints` on the backend.
 */

/**
 * Applies to `surname`, `givenName` and `middleName`.
 * Forbids control characters plus % $ ! § ` ´ < > = ? ^ ( ) | \ : * ; _
 *
 * The expression matches the empty string, so absent values are valid - presence is a separate rule.
 */
// eslint-disable-next-line no-control-regex -- the control-character range is part of the GCDM spec
export const GCDM_NAME_PATTERN = /^[^\x00-\x1F\x7F%$!§`´<>=?^()|\\:*;_]*$/;

/**
 * @returns true if the value contains at least one character GCDM rejects in a name field.
 *          Undefined and empty values are not violations.
 */
export function hasForbiddenNameCharacters(value: string | undefined): boolean {
  return !!value && !GCDM_NAME_PATTERN.test(value);
}

/**
 * `maxLength` of the GCDM fields CDF writes, keyed by the CDF field name. Used as the `maxlength`
 * attribute of the corresponding input, so an over-long value cannot be entered in the first place.
 *
 * `province` (GCDM `regionCode`) is absent on purpose: the schema declares no limit for it.
 */
export const GCDM_MAX_LENGTH = {
  firstName: 50,
  middleName: 40,
  suffix: 40,
  lastName: 50,
  street: 100,
  houseNumber: 40,
  postalCode: 10,
  city: 40,
  addressLine1: 40,
  addressLine2: 40,
  postBox: 40,
  phone: 30,
  email: 241,
  taxNumber: 30,
  nationalId: 60,
} as const;
